import {
  createDecryptStream,
  createEncryptStream,
  deriveFileKey,
  encryptedSize,
  fromBase64Url,
  generateMasterKey,
  toBase64Url,
} from "@limeshare/lsc1";
import { API_BASE } from "../config";
import { chooseSink, type Sink } from "./sinks";

const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19302" },
];

const CHUNK_SIZE = 64 * 1024; // 64 KiB DataChannel message size (safe WebRTC buffer)
const MAX_BUFFERED_AMOUNT = 2 * 1024 * 1024; // 2 MiB backpressure ceiling

export interface P2PFileMeta {
  id: string;
  name: string;
  size: number;
  type: string;
  encryptedSize: number;
}

export interface P2PProgress {
  fileIndex: number;
  fileName: string;
  bytesDone: number;
  bytesTotal: number;
}

export interface LimeDropSenderSession {
  roomId: string;
  shareUrl: string;
  masterKey: Uint8Array;
  files: File[];
  cancel: () => void;
}

/**
 * Sender starts a LimeDrop session. Prepares SDP offer, creates room, and waits for peer.
 */
export async function startLimeDropSender(
  files: File[],
  onStatusChange: (status: "waiting" | "connecting" | "transferring" | "done" | "failed", msg?: string) => void,
  onProgress: (p: P2PProgress) => void,
): Promise<LimeDropSenderSession> {
  const masterKey = generateMasterKey();
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
  const dc = pc.createDataChannel("limeshare-p2p", { ordered: true });
  dc.binaryType = "arraybuffer";

  let aborted = false;
  let pollInterval: ReturnType<typeof setInterval> | null = null;

  const cancel = () => {
    aborted = true;
    if (pollInterval) clearInterval(pollInterval);
    dc.close();
    pc.close();
  };

  const localCandidates: string[] = [];
  pc.onicecandidate = (e) => {
    if (e.candidate) localCandidates.push(JSON.stringify(e.candidate));
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // Wait brief moment for initial STUN candidates
  await new Promise((r) => setTimeout(r, 400));

  const roomRes = await fetch(`${API_BASE}/v1/drop/rooms`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ offer: offer.sdp, candidates: localCandidates }),
  });
  if (!roomRes.ok) throw new Error("Could not initialize LimeDrop room");
  const { roomId } = (await roomRes.json()) as { roomId: string };

  const shareUrl = `${window.location.origin}/drop/${roomId}#k=${toBase64Url(masterKey)}`;
  onStatusChange("waiting", "Waiting for receiver to open link…");

  // Poll for answer
  pollInterval = setInterval(async () => {
    if (aborted) return;
    try {
      const pollRes = await fetch(`${API_BASE}/v1/drop/rooms/${roomId}`);
      if (!pollRes.ok) return;
      const room = (await pollRes.json()) as { answer: string | null; candidates: string[]; state: string };

      if (room.answer && pc.signalingState !== "stable") {
        clearInterval(pollInterval!);
        pollInterval = null;
        onStatusChange("connecting", "Direct peer connection establishing…");

        await pc.setRemoteDescription(new RTCSessionDescription({ type: "answer", sdp: room.answer }));
        for (const cStr of room.candidates) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(JSON.parse(cStr)));
          } catch {
            /* ignore candidate glitch */
          }
        }
      }
    } catch {
      /* retry poll */
    }
  }, 800);

  // 15-second connection timeout -> trigger fallback
  const fallbackTimer = setTimeout(() => {
    if (dc.readyState !== "open" && !aborted) {
      onStatusChange("failed", "Direct P2P timeout. Switching to cloud fallback…");
      cancel();
    }
  }, 15_000);

  dc.onopen = async () => {
    clearTimeout(fallbackTimer);
    if (pollInterval) clearInterval(pollInterval);
    onStatusChange("transferring", "Direct P2P connection active! Encrypting & streaming…");

    try {
      await streamFilesOverDataChannel(files, masterKey, roomId, dc, onProgress);
      onStatusChange("done", "All files transferred directly peer-to-peer!");
    } catch (e) {
      onStatusChange("failed", (e as Error).message);
    }
  };

  dc.onerror = () => {
    if (!aborted) onStatusChange("failed", "DataChannel error during transmission.");
  };

  return { roomId, shareUrl, masterKey, files, cancel };
}

/**
 * Receiver connects to an existing LimeDrop room and streams incoming files to disk.
 */
export async function connectLimeDropReceiver(
  roomId: string,
  masterKey: Uint8Array,
  onMetaReceived: (meta: P2PFileMeta[]) => Promise<boolean>,
  onStatusChange: (status: "connecting" | "receiving" | "done" | "failed", msg?: string) => void,
  onProgress: (p: P2PProgress) => void,
): Promise<{ cancel: () => void }> {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
  let aborted = false;

  const cancel = () => {
    aborted = true;
    pc.close();
  };

  onStatusChange("connecting", "Connecting to sender…");

  const roomRes = await fetch(`${API_BASE}/v1/drop/rooms/${roomId}`);
  if (!roomRes.ok) throw new Error("LimeDrop room not found or expired");
  const room = (await roomRes.json()) as { offer: string; candidates: string[] };

  await pc.setRemoteDescription(new RTCSessionDescription({ type: "offer", sdp: room.offer }));

  const localCandidates: string[] = [];
  pc.onicecandidate = (e) => {
    if (e.candidate) localCandidates.push(JSON.stringify(e.candidate));
  };

  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);

  await new Promise((r) => setTimeout(r, 400));

  await fetch(`${API_BASE}/v1/drop/rooms/${roomId}/answer`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ answer: answer.sdp, candidates: localCandidates }),
  });

  for (const cStr of room.candidates) {
    try {
      await pc.addIceCandidate(new RTCIceCandidate(JSON.parse(cStr)));
    } catch {
      /* ignore */
    }
  }

  pc.ondatachannel = (e) => {
    const dc = e.channel;
    dc.binaryType = "arraybuffer";

    let filesMeta: P2PFileMeta[] = [];
    let currentFileIndex = 0;
    let currentWriter: WritableStreamDefaultWriter<Uint8Array> | null = null;
    let currentSink: Sink | null = null;
    let currentBytesDone = 0;
    let bytesTotalAll = 0;
    let bytesDoneAll = 0;

    dc.onmessage = async (evt) => {
      if (aborted) return;

      if (typeof evt.data === "string") {
        const msg = JSON.parse(evt.data) as { type: string; [k: string]: unknown };

        if (msg.type === "meta") {
          filesMeta = msg.files as P2PFileMeta[];
          bytesTotalAll = filesMeta.reduce((acc, f) => acc + f.size, 0);
          const accepted = await onMetaReceived(filesMeta);
          if (!accepted) {
            cancel();
            return;
          }
          onStatusChange("receiving", "Receiving & decrypting stream directly from peer…");
          // Signal sender to start stream
          dc.send(JSON.stringify({ type: "ready" }));
        } else if (msg.type === "start-file") {
          currentFileIndex = msg.index as number;
          const fileMeta = filesMeta[currentFileIndex]!;
          currentBytesDone = 0;

          currentSink = await chooseSink(fileMeta.name, fileMeta.size);

          const key = await deriveFileKey(masterKey, roomId, fileMeta.id);
          const decrypt = createDecryptStream({
            key,
            expectedEncryptedSize: fileMeta.encryptedSize,
            onProgress: (done) => {
              const delta = done - currentBytesDone;
              currentBytesDone = done;
              bytesDoneAll += delta;
              onProgress({
                fileIndex: currentFileIndex,
                fileName: fileMeta.name,
                bytesDone: bytesDoneAll,
                bytesTotal: bytesTotalAll,
              });
            },
          });

          // Pipe incoming stream to decrypt then to sink
          const { readable, writable } = new TransformStream<Uint8Array, Uint8Array>();
          currentWriter = writable.getWriter();
          currentSink.write(readable.pipeThrough(decrypt)).catch((err) => {
            onStatusChange("failed", `Decryption error: ${err.message}`);
          });
        } else if (msg.type === "end-file") {
          if (currentWriter) {
            await currentWriter.close();
            currentWriter = null;
          }
          dc.send(JSON.stringify({ type: "file-received", index: currentFileIndex }));
        } else if (msg.type === "all-done") {
          onStatusChange("done", "All files received and decrypted successfully!");
        }
      } else if (evt.data instanceof ArrayBuffer && currentWriter) {
        await currentWriter.write(new Uint8Array(evt.data));
      }
    };
  };

  return { cancel };
}

async function streamFilesOverDataChannel(
  files: File[],
  masterKey: Uint8Array,
  roomId: string,
  dc: RTCDataChannel,
  onProgress: (p: P2PProgress) => void,
) {
  const meta: P2PFileMeta[] = files.map((f, i) => ({
    id: `p2p_${i}`,
    name: f.name,
    size: f.size,
    type: f.type || "application/octet-stream",
    encryptedSize: encryptedSize(f.size),
  }));

  const bytesTotalAll = files.reduce((acc, f) => acc + f.size, 0);
  let bytesDoneAll = 0;

  // Send metadata
  dc.send(JSON.stringify({ type: "meta", files: meta }));

  // Wait for receiver ready ack
  await new Promise<void>((resolve) => {
    const handler = (e: MessageEvent) => {
      if (typeof e.data === "string" && JSON.parse(e.data).type === "ready") {
        dc.removeEventListener("message", handler);
        resolve();
      }
    };
    dc.addEventListener("message", handler);
  });

  for (let i = 0; i < files.length; i++) {
    const file = files[i]!;
    const fileMeta = meta[i]!;

    dc.send(JSON.stringify({ type: "start-file", index: i }));

    const key = await deriveFileKey(masterKey, roomId, fileMeta.id);
    let currentDone = 0;
    const enc = createEncryptStream({
      key,
      plaintextLength: file.size,
      onProgress: (done) => {
        const delta = done - currentDone;
        currentDone = done;
        bytesDoneAll += delta;
        onProgress({
          fileIndex: i,
          fileName: file.name,
          bytesDone: bytesDoneAll,
          bytesTotal: bytesTotalAll,
        });
      },
    });

    const stream = file.stream().pipeThrough(enc.stream);
    const reader = stream.getReader();

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;

      // Slice into 64KB chunks and apply backpressure
      for (let offset = 0; offset < value.length; offset += CHUNK_SIZE) {
        const slice = value.subarray(offset, Math.min(offset + CHUNK_SIZE, value.length));

        while (dc.bufferedAmount > MAX_BUFFERED_AMOUNT) {
          await new Promise((r) => setTimeout(r, 20));
        }
        dc.send(slice.buffer);
      }
    }

    dc.send(JSON.stringify({ type: "end-file", index: i }));

    // Wait for receiver ack for this file before moving to next
    await new Promise<void>((resolve) => {
      const handler = (e: MessageEvent) => {
        if (typeof e.data === "string") {
          const msg = JSON.parse(e.data);
          if (msg.type === "file-received" && msg.index === i) {
            dc.removeEventListener("message", handler);
            resolve();
          }
        }
      };
      dc.addEventListener("message", handler);
    });
  }

  dc.send(JSON.stringify({ type: "all-done" }));
}
